import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

function TotalIvGenerationChart({ chartData }) {
  const labels = useMemo(() => chartData.map(d => d.time), [chartData]);

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'bar',
        label: '발전량 (kW)',
        data: chartData.map(d => (d.gridPower ?? 0) / 1000),
        backgroundColor: 'rgba(150, 120, 90, 0.5)',
        yAxisID: 'y',
        borderRadius: 4,
        //간격
        categoryPercentage: 0.8, // 기본 ~0.8, 더 작게=간격 넓게
        barPercentage: 0.9,      // 기본 ~0.9, 더 작게=막대 얇게(간격 넓어짐)
      },
      {
        type: 'line',
        label: '경사 일사량 (W/m²)',
        data: chartData.map(d => d.tiltedIrradiance),
        borderColor: 'rgba(100, 140, 190, 1)',
        backgroundColor: 'rgba(100, 140, 190, 0.2)',
        yAxisID: 'y1',
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        type: 'line',
        label: '수평 일사량 (W/m²)',
        data: chartData.map(d => d.horizontalIrradiance),
        borderColor: 'rgba(120, 170, 170, 1)',
        backgroundColor: 'rgba(120, 170, 170, 0.2)',
        yAxisID: 'y1',
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        type: 'line',
        label: '외기 온도 (℃)',
        data: chartData.map(d => d.outdoorTemp),
        borderColor: 'rgba(180, 120, 130, 1)',
        backgroundColor: 'rgba(180, 120, 130, 0.2)',
        yAxisID: 'y1',
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        type: 'line',
        label: '표면 온도 (℃)',
        data: chartData.map(d => d.surfaceTemp),
        borderColor: 'rgba(160, 140, 200, 1)',
        backgroundColor: 'rgba(160, 140, 200, 0.2)',
        yAxisID: 'y1',
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2
      }
    ]
  }), [labels, chartData]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,                // 초기 렌더 가속
    elements: { point: { radius: 0 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#333', font: { size: 12, family: 'Pretendard' } },
      },
      tooltip: {
        animation: false,
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label || '';
            const value = ctx.raw;
            if (label === '발전량 (kW)') {
              const n = Number(value);
              return `${label}: ${Number.isFinite(n) ? n.toFixed(2) : 0} kW`;
            }
            return `${label}: ${value ?? '-'}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: '시간', color: '#666', font: { size: 14, family: 'Pretendard' } },
        ticks: { color: '#999' },
        grid: { display: false },
      },
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: '발전량 (kW)', color: '#666', font: { size: 14, family: 'Pretendard' } },
        ticks: { color: '#888' },
        grid: { color: 'rgba(200, 200, 200, 0.1)' },
        min: 0,
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: '환경 센서 값', color: '#666', font: { size: 14, family: 'Pretendard' } },
        ticks: { color: '#888' },
        grid: { drawOnChartArea: false },
        min: 0,
      },
    },
  }), []);

  return (
    <div className="chart-wrapper">
      <Chart type="bar" data={data} options={options} />
    </div>
  );
}

export default TotalIvGenerationChart;
